import { useEffect, useRef, useState } from 'react';
import { Coordinates } from '@property/shared';
import { loadGoogleMaps } from '@/lib/google-maps-loader';

// Dark theme styles for Google Maps
export const darkMapStyles: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#263c3f' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6b9a76' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#38414e' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#212a37' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9ca5b3' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#746855' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#1f2835' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#f3d19c' }],
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#2f3948' }],
  },
  {
    featureType: 'transit.station',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#17263c' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#515c6d' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#17263c' }],
  },
];

export interface UseStaticMapOptions {
  apiKey: string;
  center: Coordinates;
  zoom?: number;
  isDarkMode?: boolean;
  showUserLocation?: boolean;
}

export interface UseStaticMapReturn {
  mapRef: React.RefObject<HTMLDivElement | null>;
  isReady: boolean;
  userLocation: Coordinates | null;
}

export function useStaticMap({
  apiKey,
  center,
  zoom = 17,
  isDarkMode = false,
  showUserLocation = true,
}: UseStaticMapOptions): UseStaticMapReturn {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const userMarkerRef = useRef<google.maps.Marker | null>(null);
  // Mirrors isDarkMode for the init effect below without needing it in that effect's deps
  // (re-running the init effect on every dark-mode toggle would reinitialize the whole map;
  // the dedicated effect above already keeps styles in sync live via setOptions).
  const isDarkModeRef = useRef(isDarkMode);
  isDarkModeRef.current = isDarkMode;

  const [isReady, setIsReady] = useState(false);
  const [userLocation, setUserLocation] = useState<Coordinates | null>(null);

  // Update map styles when dark mode changes
  useEffect(() => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setOptions({
        styles: isDarkMode ? darkMapStyles : [],
      });
    }
  }, [isDarkMode]);

  useEffect(() => {
    if (!mapRef.current) return;

    let mounted = true;

    const initMap = async () => {
      try {
        await loadGoogleMaps(apiKey);

        if (!mounted || !mapRef.current) return;

        mapInstanceRef.current = new google.maps.Map(mapRef.current, {
          center: { lat: center.lat, lng: center.lon },
          zoom,
          mapTypeId: 'roadmap',
          styles: isDarkModeRef.current ? darkMapStyles : [],
          // Disable panning but allow zoom
          disableDefaultUI: true,
          zoomControl: true,
          zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_BOTTOM,
          },
          gestureHandling: 'none',
          scrollwheel: false,
          disableDoubleClickZoom: true,
          draggable: false,
          keyboardShortcuts: false,
        });

        // Add marker at the center (property location)
        markerRef.current = new google.maps.Marker({
          position: { lat: center.lat, lng: center.lon },
          map: mapInstanceRef.current,
          title: 'Property Location',
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#3B82F6',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 3,
          },
        });

        // Get user location if enabled
        if (showUserLocation && navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              if (!mounted || !mapInstanceRef.current) return;

              const userCoords = {
                lat: position.coords.latitude,
                lon: position.coords.longitude,
              };
              setUserLocation(userCoords);

              // Add user location marker
              userMarkerRef.current = new google.maps.Marker({
                position: { lat: userCoords.lat, lng: userCoords.lon },
                map: mapInstanceRef.current,
                title: 'Your Location',
                icon: {
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: '#22C55E',
                  fillOpacity: 1,
                  strokeColor: '#FFFFFF',
                  strokeWeight: 2,
                },
              });
            },
            () => {
              // Silently fail if geolocation is denied
            },
            { enableHighAccuracy: true, timeout: 5000 },
          );
        }

        setIsReady(true);
      } catch (error) {
        console.error('Failed to load Google Maps:', error);
      }
    };

    initMap();

    return () => {
      mounted = false;
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
      if (userMarkerRef.current) {
        userMarkerRef.current.setMap(null);
      }
    };
  }, [apiKey, center.lat, center.lon, zoom, showUserLocation]);

  return {
    mapRef,
    isReady,
    userLocation,
  };
}
